// Note List Component with Grid/List Toggle, Filters, and Bulk Actions
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNotes } from '../../contexts/NotesContext.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useFolders } from '../../contexts/FoldersContext.jsx';
import { useTags } from '../../contexts/TagsContext.jsx';
import { useOrganization } from '../../contexts/OrganizationContext.jsx';
import { bulkFavorite, bulkArchive, bulkDelete } from '../../services/firestore.js';
import { groupNotesByDate, groupNotesByDomain } from '../../utils/noteGrouping.js';
import NoteCard from './NoteCard.jsx';
import Icon from '../common/Icon';
import './NoteList.css';

export default function NoteList({ onSelectNote, selectedNoteId, onShare, onCreateNote, isCollapsed = false, onToggleCollapse }) {
    const { user } = useAuth();
    const { allNotes, loading, syncNotes } = useNotes();
    const { selectedFolderId, getFolderById, selectFolder } = useFolders();
    const { selectedTagIds, getTagById, toggleTagFilter } = useTags();
    const {
        viewMode,
        toggleViewMode,
        filters,
        updateFilters,
        selectedNoteIds,
        toggleNoteSelection,
        selectAllNotes,
        clearSelection,
        isNoteSelected
    } = useOrganization();

    const [localSearchQuery, setLocalSearchQuery] = useState(filters.searchQuery || '');
    const [groupBy, setGroupBy] = useState('none'); // 'none', 'date', 'domain'

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            updateFilters({ searchQuery: localSearchQuery });
        }, 300);

        return () => clearTimeout(timer);
    }, [localSearchQuery, updateFilters]);

    // Filtered and sorted notes
    const filteredNotes = useMemo(() => {
        let result = [...allNotes];

        // Filter by status
        if (filters.status === 'active') {
            result = result.filter(n => !n.isArchived && !n.isDeleted);
        } else if (filters.status === 'favorites') {
            result = result.filter(n => n.isFavorite && !n.isArchived && !n.isDeleted);
        } else if (filters.status === 'archived') {
            result = result.filter(n => n.isArchived && !n.isDeleted);
        } else if (filters.status === 'deleted') {
            result = result.filter(n => n.isDeleted);
        }

        // Filter by folder
        if (selectedFolderId) {
            result = result.filter(n => n.folderId === selectedFolderId);
        }

        // Filter by tags (AND logic - must have all selected tags)
        if (selectedTagIds.length > 0) {
            result = result.filter(n =>
                selectedTagIds.every(tagId => n.tagIds?.includes(tagId))
            );
        }

        // Filter by search query
        if (filters.searchQuery) {
            const query = filters.searchQuery.toLowerCase();
            result = result.filter(n =>
                n.title?.toLowerCase().includes(query) ||
                n.content?.toLowerCase().includes(query) ||
                n.key?.toLowerCase().includes(query) ||
                n.domain?.toLowerCase().includes(query)
            );
        }

        // Filter by date range
        if (filters.dateRange?.start) {
            result = result.filter(n => {
                const noteDate = n.updatedAt?.toDate?.() || new Date(n.updatedAt || 0);
                return noteDate >= filters.dateRange.start;
            });
        }
        if (filters.dateRange?.end) {
            result = result.filter(n => {
                const noteDate = n.updatedAt?.toDate?.() || new Date(n.updatedAt || 0);
                return noteDate <= filters.dateRange.end;
            });
        }

        // Sort
        result.sort((a, b) => {
            let aVal, bVal;
            switch (filters.sortBy) {
                case 'title':
                    aVal = a.title || a.key || '';
                    bVal = b.title || b.key || '';
                    break;
                case 'createdAt':
                    aVal = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
                    bVal = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
                    break;
                case 'updatedAt':
                default:
                    aVal = a.updatedAt?.toDate?.() || new Date(a.updatedAt || a.createdAt || 0);
                    bVal = b.updatedAt?.toDate?.() || new Date(b.updatedAt || b.createdAt || 0);
            }

            if (filters.sortOrder === 'asc') {
                return aVal > bVal ? 1 : -1;
            }
            return aVal < bVal ? 1 : -1;
        });

        return result;
    }, [allNotes, filters, selectedFolderId, selectedTagIds]);

    // Grouped notes
    const groupedNotes = useMemo(() => {
        if (groupBy === 'none') {
            return null;
        } else if (groupBy === 'date') {
            return groupNotesByDate(filteredNotes);
        } else if (groupBy === 'domain') {
            return groupNotesByDomain(filteredNotes);
        }
        return null;
    }, [filteredNotes, groupBy]);

    // Header content
    const getHeaderTitle = () => {
        if (selectedFolderId) {
            const folder = getFolderById(selectedFolderId);
            return (
                <span className="flex items-center gap-2">
                    <Icon name={folder?.icon ? "Folder" : "Folder"} size="md" />
                    {folder?.name || 'Folder'}
                </span>
            );
        }

        switch (filters.status) {
            case 'favorites': return <span className="flex items-center gap-2"><Icon name="Star" className="text-warning" /> Favorites</span>;
            case 'archived': return <span className="flex items-center gap-2"><Icon name="Archive" /> Archived</span>;
            case 'deleted': return <span className="flex items-center gap-2"><Icon name="Trash2" className="text-danger" /> Deleted</span>;
            default: return <span className="flex items-center gap-2"><Icon name="Files" /> All Notes</span>;
        }
    };

    // Bulk actions
    const handleBulkFavorite = useCallback(async (isFavorite) => {
        if (selectedNoteIds.length === 0 || !user) return;
        await bulkFavorite(user.uid, selectedNoteIds, isFavorite);
        clearSelection();
        syncNotes();
    }, [selectedNoteIds, user, clearSelection, syncNotes]);

    const handleBulkArchive = useCallback(async () => {
        if (selectedNoteIds.length === 0 || !user) return;
        await bulkArchive(user.uid, selectedNoteIds);
        clearSelection();
        syncNotes();
    }, [selectedNoteIds, user, clearSelection, syncNotes]);

    const handleBulkDelete = useCallback(async () => {
        if (selectedNoteIds.length === 0 || !user) return;
        if (!confirm(`Delete ${selectedNoteIds.length} notes?`)) return;
        await bulkDelete(user.uid, selectedNoteIds);
        clearSelection();
        syncNotes();
    }, [selectedNoteIds, user, clearSelection, syncNotes]);

    const handleSelectAll = useCallback(() => {
        const noteIds = filteredNotes.map(n => n.id);
        selectAllNotes(noteIds);
    }, [filteredNotes, selectAllNotes]);

    const handleClearFolderFilter = useCallback(() => {
        selectFolder(null);
    }, [selectFolder]);

    const handleClearTagFilter = useCallback((tagId) => {
        toggleTagFilter(tagId);
    }, [toggleTagFilter]);

    // Render group helper
    const renderNoteGroup = (notes, groupTitle) => {
        if (!notes || notes.length === 0) return null;
        return (
            <div className={`note-group ${groupTitle === 'Today' ? 'group-today' : ''}`} key={groupTitle}>
                <h3 className="group-title">
                    {groupTitle}
                    <span className="group-count">{notes.length}</span>
                </h3>
                <div className={`notes-container ${viewMode}`}>
                    {notes.map(note => (
                        <NoteCard
                            key={note.id || note.key}
                            note={note}
                            isSelected={selectedNoteId === note.id || selectedNoteId === note.key}
                            isChecked={isNoteSelected(note.id)}
                            onSelect={() => onSelectNote?.(note)}
                            onToggleCheck={() => toggleNoteSelection(note.id)}
                            viewMode={viewMode}
                            onShare={() => onShare?.(note)}
                        />
                    ))}
                </div>
            </div>
        );
    };

    // Collapsed view - show only expand button
    if (isCollapsed) {
        return (
            <div className="note-list-container collapsed">
                <div className="collapsed-toolbar">
                    <button
                        className="collapse-toggle-btn"
                        onClick={onToggleCollapse}
                        title="Expand notes list"
                    >
                        <Icon name="ChevronRight" size="sm" />
                    </button>
                    <button
                        className="collapsed-action-btn"
                        onClick={onCreateNote}
                        title="Create new note"
                    >
                        <Icon name="Plus" size="sm" />
                    </button>
                    <div className="collapsed-note-count" title={`${filteredNotes.length} notes`}>
                        <Icon name="FileText" size="sm" />
                        <span>{filteredNotes.length}</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="note-list-container">
            {/* Compact header - single row */}
            <div className="note-list-header">
                {/* Collapse toggle button */}
                {onToggleCollapse && (
                    <button
                        className="collapse-toggle-btn"
                        onClick={onToggleCollapse}
                        title="Collapse notes list"
                    >
                        <Icon name="ChevronLeft" size="sm" />
                    </button>
                )}
                
                {/* Title & count */}
                <div className="header-title">
                    {getHeaderTitle()}
                    <span className="note-count">{filteredNotes.length}</span>
                    {/* Create Note button */}
                    <button
                        className="create-note-btn"
                        onClick={onCreateNote}
                        title="Create new note"
                    >
                        <Icon name="Plus" size="sm" />
                        <span className="create-note-text">New</span>
                    </button>
                </div>

                {/* Controls */}
                <div className="header-controls">
                    {/* Search */}
                    <div className="search-box">
                        <Icon name="Search" size="sm" className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search..."
                            value={localSearchQuery}
                            onChange={(e) => setLocalSearchQuery(e.target.value)}
                            className="search-input"
                        />
                        {localSearchQuery && (
                            <button className="clear-search-btn" onClick={() => setLocalSearchQuery('')}>
                                <Icon name="X" size="xs" />
                            </button>
                        )}
                    </div>

                    {/* Combined Sort/Group select */}
                    <select
                        className="compact-select"
                        value={groupBy === 'none' ? `sort-${filters.sortBy}-${filters.sortOrder}` : `group-${groupBy}`}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val.startsWith('group-')) {
                                setGroupBy(val.replace('group-', ''));
                            } else {
                                setGroupBy('none');
                                const [, sortBy, sortOrder] = val.split('-');
                                updateFilters({ sortBy, sortOrder });
                            }
                        }}
                    >
                        <optgroup label="Sort">
                            <option value="sort-updatedAt-desc">Updated ↓</option>
                            <option value="sort-updatedAt-asc">Updated ↑</option>
                            <option value="sort-createdAt-desc">Created ↓</option>
                            <option value="sort-createdAt-asc">Created ↑</option>
                            <option value="sort-title-asc">Title A-Z</option>
                            <option value="sort-title-desc">Title Z-A</option>
                        </optgroup>
                        <optgroup label="Group">
                            <option value="group-date">By Date</option>
                            <option value="group-domain">By Domain</option>
                        </optgroup>
                    </select>

                    {/* Single view toggle button */}
                    <button
                        className="view-toggle-btn"
                        onClick={() => toggleViewMode()}
                        title={viewMode === 'grid' ? 'Switch to list view' : 'Switch to grid view'}
                    >
                        <Icon name={viewMode === 'grid' ? 'List' : 'Grid3x3'} size="sm" />
                    </button>
                </div>
            </div>

            {/* Active filter pills */}
            {(selectedFolderId || selectedTagIds.length > 0) && (
                <div className="active-filters">
                    {selectedFolderId && (
                        <span className="filter-pill folder-pill">
                            <Icon name="Folder" size="sm" />
                            {getFolderById(selectedFolderId)?.name || 'Folder'}
                            <button onClick={handleClearFolderFilter}><Icon name="X" size="sm" /></button>
                        </span>
                    )}
                    {selectedTagIds.map(tagId => {
                        const tag = getTagById(tagId);
                        return tag ? (
                            <span
                                key={tagId}
                                className="filter-pill tag-pill"
                                style={{ borderColor: tag.color || '#ffaa00' }}
                            >
                                <span className="tag-dot" style={{ backgroundColor: tag.color || '#ffaa00' }} />
                                {tag.name}
                                <button onClick={() => handleClearTagFilter(tagId)}><Icon name="X" size="sm" /></button>
                            </span>
                        ) : null;
                    })}
                </div>
            )}

            {/* Bulk action toolbar */}
            {selectedNoteIds.length > 0 && (
                <div className="bulk-action-bar">
                    <div className="bulk-left">
                        <input
                            type="checkbox"
                            checked={selectedNoteIds.length === filteredNotes.length && filteredNotes.length > 0}
                            onChange={() => {
                                if (selectedNoteIds.length === filteredNotes.length) {
                                    clearSelection();
                                } else {
                                    handleSelectAll();
                                }
                            }}
                        />
                        <span className="selected-count">
                            {selectedNoteIds.length} selected
                        </span>
                    </div>

                    <div className="bulk-actions">
                        <button onClick={() => handleBulkFavorite(true)} title="Favorite">
                            <Icon name="Star" size="sm" />
                        </button>
                        <button onClick={handleBulkArchive} title="Archive">
                            <Icon name="Archive" size="sm" />
                        </button>
                        <button onClick={handleBulkDelete} title="Delete" className="delete-btn">
                            <Icon name="Trash2" size="sm" />
                        </button>
                        <button onClick={clearSelection} title="Cancel">
                            <Icon name="X" size="sm" />
                        </button>
                    </div>
                </div>
            )}

            {/* Notes grid/list */}
            {loading ? (
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <div>Loading notes...</div>
                </div>
            ) : filteredNotes.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">
                        <Icon name="FileText" size="xl" />
                    </div>
                    <p className="empty-text">No notes found</p>
                    <p className="empty-hint">
                        {filters.searchQuery
                            ? 'Try a different search term'
                            : 'Create your first note'}
                    </p>
                </div>
            ) : (
                <div className="notes-scroll-container">
                    {groupBy === 'none' ? (
                        <div className={`notes-container ${viewMode}`}>
                            {filteredNotes.map(note => (
                                <NoteCard
                                    key={note.id || note.key}
                                    note={note}
                                    isSelected={selectedNoteId === note.id || selectedNoteId === note.key}
                                    isChecked={isNoteSelected(note.id)}
                                    onSelect={() => onSelectNote?.(note)}
                                    onToggleCheck={() => toggleNoteSelection(note.id)}
                                    viewMode={viewMode}
                                    onShare={() => onShare?.(note)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="grouped-notes-list">
                            {groupBy === 'date' && groupedNotes && (
                                <>
                                    {renderNoteGroup(groupedNotes.today, 'Today')}
                                    {renderNoteGroup(groupedNotes.yesterday, 'Yesterday')}
                                    {renderNoteGroup(groupedNotes.thisWeek, 'This Week')}
                                    {renderNoteGroup(groupedNotes.thisMonth, 'This Month')}
                                    {renderNoteGroup(groupedNotes.older, 'Older')}
                                </>
                            )}
                            {groupBy === 'domain' && groupedNotes && (
                                Object.entries(groupedNotes).map(([domain, notes]) =>
                                    renderNoteGroup(notes, domain)
                                )
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}